/**
 * 埋点整体方案：
    进入就初始化埋点对象
    切换游戏时，设置gameInfo
    进入相关页面时，设置埋点数据对象
    页面直接埋点
  https://doc.weixin.qq.com/sheet/e3_m_EUwZMEXWkqiH?scode=AJEAIQdfAAoid26XyvACsADgYIACk&tab=BB08J2
 */

import { useEffect } from 'react';
import {
  clearFilterBuriedPoint,
  setFilterPointLinkId,
} from '@op/components/FilterCommon/utils/point';
import { operationLog } from '@op/services';

export type BuriedPointType = {
  pageName: string;
  pageId: string;
  pagePath: string;
  common: {
    source: number;
    typeId: string;
  };
  button?: { [key: string]: { [key: string]: any } };
  search?: { [key: string]: { [key: string]: any } };
  user?: { [key: string]: { [key: string]: any } };
  card?: { [key: string]: { [key: string]: any } };
  table?: { [key: string]: { [key: string]: any } };
};

export type GameInfoType = {
  edition_unified_id: string;
  id_type: string;
  entity_type: string;
};

function getUserInfo() {
  try {
    const data = sessionStorage.getItem('openId');
    return data?.replace(/"/g, '');
  } catch (error) {}
}

class BuriedPoint {
  private buriedPointInfo: BuriedPointType;
  private isOnline = 'logIn';

  constructor(buriedPoint?: BuriedPointType) {
    this.buriedPointInfo = buriedPoint || ({} as any);
  }

  setOnline(value: 'logIn' | 'logOut') {
    this.isOnline = value;
  }

  setBuriedPointInfo(value: BuriedPointType) {
    this.buriedPointInfo = value;
  }

  buriedBtnPoint(
    key: string,
    extInfo: string | { [key: string]: string },
    extInfo2?: string,
    extInfo3?: string,
  ) {
    const { common = {}, pageId, button } = this.buriedPointInfo || {};
    const btnInfo = button?.[key];
    if (!btnInfo) {
      return this.error(key, 'button');
    }
    let realExtInfo: any = extInfo;
    try {
      if (typeof extInfo !== 'object') {
        realExtInfo = { value: extInfo };
      }
      realExtInfo = JSON.stringify(realExtInfo);
    } catch (error) {}

    const buttonLog = {
      pageId,
      extInfo: realExtInfo,
      extInfo2,
      extInfo3,
      ...common,
      ...btnInfo,
    };
    this.toServer({
      logType: 'buttonLog',
      buttonLog,
    });
  }

  buriedPagePoint(extInfo?: string, newPageName?: string) {
    let { pageName } = this.buriedPointInfo || {};
    const { common = {}, pageId, pagePath } = this.buriedPointInfo || {};
    pageName = newPageName || pageName;

    const pageLog = {
      ...common,
      extInfo,
      isOnline: this.isOnline,
      pageId,
      pageName,
      pagePath,
    };
    this.toServer({ logType: 'pageLog', pageLog });
  }

  buriedCommonBtnPoint(
    commonComp: 'card' | 'table',
    commonId: string,
    key: string,
    extInfo: string | { [key: string]: string },
  ) {
    const {
      common = {},
      pageId,
      [commonComp]: comp,
    } = this.buriedPointInfo || {};
    const button = comp?.[commonId];
    if (!button) {
      return this.error(key, 'commonComp');
    }
    const btnInfo = button?.[key];
    if (!btnInfo) {
      return this.error(key, 'commonButton');
    }
    let realExtInfo: any = extInfo;
    try {
      if (typeof extInfo !== 'object') {
        realExtInfo = { value: extInfo };
      }
      realExtInfo = JSON.stringify(realExtInfo);
    } catch (error) {}

    const buttonLog = {
      pageId,
      extInfo: realExtInfo,
      ...common,
      ...btnInfo,
    };
    this.toServer({ logType: 'buttonLog', buttonLog });
  }

  private toServer(params: any) {
    operationLog({
      openId: getUserInfo(),
      ...params,
    }).catch(() => {});
  }

  private error(key: string, type: string) {
    console.error(
      `${type} buried point not found: ${key} is not in the ${type} buried point, please check!`,
    );
  }
}

let buriedPointInfo: null | BuriedPoint = null;

function defaultFunc() {
  console.error('请先初始化埋点对象');
}

function resultBuried(realFunc?: (...item: any) => void, ...params: any) {
  if (buriedPointInfo === null) {
    return defaultFunc();
  }
  realFunc?.call(buriedPointInfo, ...params);
}

// 按钮埋点
function buriedBtnPoint(
  key: string,
  extInfo?: { [key: string]: any } | string | number | boolean,
  extInfo2?: string,
  extInfo3?: string,
) {
  return resultBuried(
    buriedPointInfo?.buriedBtnPoint,
    key,
    extInfo,
    extInfo2,
    extInfo3,
  );
}

// 按钮埋点
function buriedCardBtnPoint(
  commonId: string,
  key: string,
  extInfo?: { [key: string]: string | any } | string,
) {
  const commonComp = 'card';
  return resultBuried(
    buriedPointInfo?.buriedCommonBtnPoint,
    commonComp,
    commonId,
    key,
    extInfo,
  );
}

// 按钮埋点
function buriedTableBtnPoint(
  commonId: string,
  key: string,
  extInfo?: { [key: string]: string | any } | string,
) {
  const commonComp = 'table';
  return resultBuried(
    buriedPointInfo?.buriedCommonBtnPoint,
    commonComp,
    commonId,
    key,
    extInfo,
  );
}

// 按钮埋点
function buriedCommonBtnPoint(
  commonComp: string,
  commonId: string,
  key: string,
  extInfo?: { [key: string]: string | any } | string,
) {
  return resultBuried(
    buriedPointInfo?.buriedCommonBtnPoint,
    commonComp,
    commonId,
    key,
    extInfo,
  );
}

// 页面埋点
function buriedPagePoint(extInfo?: string, pageName?: string) {
  return resultBuried(buriedPointInfo?.buriedPagePoint, extInfo, pageName);
}

/**
 * 埋点初始化信息
 * 这个方法在src\pages\opinion\models\common\index.tsx 进行调用
 * @param buriedPoint
 * @param gameInfo
 * @returns
 */
function initBuriedPoint(buriedPoint?: BuriedPointType) {
  if (buriedPointInfo === null) {
    console.log('埋点对象初始化成功');
    buriedPointInfo = new BuriedPoint(buriedPoint!);
  }
  return buriedPointInfo;
}

/**
 * 设置埋点信息
 * @param buriedPoint
 * @returns
 */
function setBuriedPointBuriedPoint(buriedPoint: BuriedPointType) {
  return resultBuried(buriedPointInfo?.setBuriedPointInfo, buriedPoint);
}

/**
 * 设置埋点数据信息，这个需要在每个页面里面调用
 * @param buriedPoint
 * @returns
 */
function useInitBuriedPointInfo({
  buriedPoint,
  triggerPagePoint = true,
  pageExtInfo,
}: {
  buriedPoint: BuriedPointType;
  triggerPagePoint?: boolean;
  pageExtInfo?: string;
}) {
  useEffect(() => {
    if (!buriedPoint) return;
    // 设置游戏信息和埋点信息
    setBuriedPointBuriedPoint(buriedPoint);

    if (triggerPagePoint) {
      buriedPagePoint(pageExtInfo);
    }
  }, [buriedPoint]);

  useEffect(() => {
    setFilterPointLinkId();

    return () => {
      clearFilterBuriedPoint();
    };
  }, [buriedPoint]);
}

// 埋点对象
export const DBOM_POINT = {
  btn: buriedBtnPoint,
  cardBtn: buriedCardBtnPoint,
  tableBtn: buriedTableBtnPoint,
  commonBtn: buriedCommonBtnPoint,
  page: buriedPagePoint,
  init: initBuriedPoint,
  useInitPage: useInitBuriedPointInfo,
};
